import {
  IProductCategory,
  IProductDeliveryInfo,
  IProductTagEntry,
  TProductType,
} from 'types/contentful/productCategory'
import flatMap from 'lodash/flatMap'
import nth from 'lodash/nth'
import unionBy from 'lodash/unionBy'
import {
  IMarketingCategoryCard,
  IOptionToShow,
  IProductInfoFromContentful,
  TProductTag,
} from '../_types'
import { CategoryPage } from 'types/contentful/categoryPage'
import { Entry } from 'contentful'
import { isEmpty } from 'lodash'
import { loadAllProductSlugsFromContentful } from '@utils/contentful/load-all-product-slugs'
import { getProductsWithPrice } from '@utils/platform/products'
import {
  TProductTileData,
  TProductVariantDetails,
} from '../_components/ProductTile/ProductTile.types'
import { IStyledContentBlock } from 'types/contentful/styledContentBlock'
import {
  IImage,
  mapContentfulImageAsset,
} from '@utils/contentful/contentfulImage'
import { getDateFromToday } from 'app/(main)/(catalog)/_components/product/ProductPage/productDetailVariants/ProductDeliveryInfo'

// filter through the categories list, and only take those where
// the category slug matches the productSlugsInSelectedCategory
export function hydrateSelectedProductCategoriesWithBcData<
  T extends { productSlug: string }
>(productSlugsInSelectedCategory: T[], categories) {
  const categoryListInBigCommerce: {
    entityId: number
    name: string
    path: string
    description: string
    productCount: number
  }[] = flatMap(categories, (category) => category.children)

  const categoryListInBigCommerceWithSlug = categoryListInBigCommerce.map(
    (category) => ({
      ...category,
      slug: nth(category.path.split('/'), -2),
    })
  )

  const productsInSelectedCategory = productSlugsInSelectedCategory?.map(
    (productCategory) => {
      const categoryInBigCommerce = categoryListInBigCommerceWithSlug.find(
        (category) => category?.slug === productCategory?.productSlug
      )
      return { ...productCategory, ...categoryInBigCommerce }
    }
  )

  return productsInSelectedCategory
}

export const getMinPriceOfProductVariants = (variants) =>
  Math.min(...variants.map((variant) => variant?.price || variant?.retailPrice))

export const getMinPricePerUnit = (variants) => {
  return Math.min(
    ...variants
      .map((variant) => {
        const qtyNumeric =
          typeof variant.quantity === 'number'
            ? variant.quantity
            : Number(variant.quantity.match(/^\d+/)[0])
        return +(variant.price || variant.retailPrice) / qtyNumeric
      })
      .filter(Boolean)
  )
}

export const getMinSalePricePerUnit = (variants) => {
  return Math.min(
    ...variants
      .map((variant) => {
        return +variant?.salePrice / +variant?.quantity
      })
      .filter(Boolean)
  )
}

export const getMinSalePriceOfProductVariants = (variants) =>
  Math.min(...variants.map((variant) => variant?.salePrice).filter(Number))

export const getMinQuantityOfProductVariants = (variants) => {
  const minQuantity = Math.min(
    ...variants
      .map((variant) => {
        if (
          variant?.quantity?.toLowerCase()?.includes('sample') ||
          variant?.variantSku?.toLowerCase()?.includes('sample')
        ) {
          return null
        }
        return variant?.quantity
      })
      .filter(Boolean)
  )
  return minQuantity === Infinity ? null : minQuantity
}

const getCountOfVariantOptions = (variantToGet: string, variants: any[]) =>
  unionBy(variants, variantToGet)
    .map((variant) => variant[variantToGet])
    .filter(Boolean)?.length

export const optionToShowToString = (
  optionToShow: IOptionToShow,
  variants: any[],
  productType: TProductType,
  prefixedString = ''
) => {
  if (productType === 'Plus') {
    return ''
  }

  const optionName = optionToShow?.optionName || 'size'
  const optionTitle = optionToShow?.optionTitle || 'Size'

  const numberOfOptions = getCountOfVariantOptions(optionName, variants)
  const optionLabel = numberOfOptions === 1 ? optionTitle : `${optionTitle}s`

  return numberOfOptions > 0
    ? `${prefixedString} ${numberOfOptions} ${optionLabel}`
    : ''
}

const mapMarketingCard = (
  entry: Entry<IStyledContentBlock>
): IMarketingCategoryCard => {
  return {
    backgroundColor: entry.fields?.backgroundColor || null,
    title: entry.fields.title || null,
    titleColor: entry.fields.titleColor || null,
    titleSize: entry.fields?.titleSize || null,
    subtitle: entry.fields?.subtitle || null,
    subtitleColor: entry.fields?.subtitleColor || null,
    desktopImage: mapContentfulImageAsset(entry?.fields?.images?.[0]) || null,
    mobileImage: mapContentfulImageAsset(entry?.fields?.images?.[1]) || null,
    desktopLayout: entry?.fields?.desktopLayout,
    mobileLayout: entry?.fields?.mobileLayout,
    link: entry?.fields?.blockLink || null,
    ctaLabel: entry?.fields?.blockCtaLabel || null,
  }
}

export const parseCategoryMarketingCards = (
  allCards: (Entry<IProductCategory> | Entry<IStyledContentBlock>)[]
): IMarketingCategoryCard[] => {
  return (
    allCards
      ?.map((card, index) => {
        if (card.sys.contentType.sys.id === 'styledContentBlock') {
          return {
            ...mapMarketingCard(card as Entry<IStyledContentBlock>),
            index,
          }
        }
      })
      .filter(Boolean) || []
  )
}

export const getDeliveryInfo = (
  deliveryTypes: (Omit<IProductDeliveryInfo, 'deliveryTypeIcon'> & {
    deliveryTypeIcon: IImage
  })[]
) => {
  let deliveryInfo = ''

  if (deliveryTypes?.length) {
    const fastestMinDeliveryDays = Math.min(
      ...deliveryTypes?.map((type) => type.minDays)
    )
    const fastestMaxDeliveryDays = Math.min(
      ...deliveryTypes?.map((type) => type.maxDays)
    )
    const minWeeks = Math.round(fastestMinDeliveryDays / 7)
    const maxWeeks = Math.round(fastestMaxDeliveryDays / 7)

    if (maxWeeks === 0) {
      // If max delivery date is less than week, display single dates instead: "11 Aug - 13 Aug"
      deliveryInfo = `Delivery: ${getDateFromToday(
        fastestMinDeliveryDays
      )} - ${getDateFromToday(fastestMaxDeliveryDays)}`
    } else if (minWeeks === maxWeeks || maxWeeks === 1) {
      const weeksInCorrentGrammaticalNumber = maxWeeks > 1 ? 'weeks' : 'week'
      deliveryInfo = `Delivery: ${maxWeeks} ${weeksInCorrentGrammaticalNumber}`
    } else {
      deliveryInfo = `Delivery: ${minWeeks || 1} - ${maxWeeks} weeks`
    }
  }

  return deliveryInfo
}

export const getProductVariantDetails = (
  variants: any[]
): TProductVariantDetails => {
  const minOrderQuantity = getMinQuantityOfProductVariants(variants)
  const pricePerUnit = getMinPricePerUnit(variants)
  const salePricePerUnit = getMinSalePricePerUnit(variants)

  return { minOrderQuantity, pricePerUnit, salePricePerUnit }
}

export const productDataToProductTile = (
  product: IProductInfoFromContentful & {
    entityId?: number
    name?: string
    path?: string
    description?: string
    productCount?: number
    variants: any[]
  }
): TProductTileData => {
  const deliveryInfo = getDeliveryInfo(product.productDeliveryTypes)

  return {
    productName: product?.displayName,
    primaryImage: product?.image,
    imageBackgroundColor: product?.imageBackgroundColour,
    imageTags:
      [product?.statusTag || null, product.saleTag || null].filter(Boolean) ||
      [],
    productVariantsDetails: product?.variants?.length
      ? getProductVariantDetails(product?.variants)
      : null,
    minOrderQuantityFallback: product?.moq || null,
    linkUrl: product?.productUrl,
    productType: product?.productType,
    urlSlug: product?.urlSlug,
    productSlug: product?.productSlug,
    productTags: product?.productTags?.filter(Boolean) || [],
    deliveryInfo: deliveryInfo,
  }
}

export const mapTagEntry = (tagEntry: Entry<IProductTagEntry>): TProductTag => {
  if (!tagEntry) {
    return null
  }

  return {
    ...tagEntry?.fields,
    labelImage: mapContentfulImageAsset(tagEntry?.fields?.labelImage),
    isVisible: tagEntry?.fields?.visibilityPerRegion === false ? false : true,
  }
}

export const mapProductContentfulData = (
  product: Entry<IProductCategory>,
  allProductSlugs
) => {
  // if the product has been changed to DRAFT, we'll got {} product
  if (isEmpty(product)) {
    return null
  }

  return {
    urlSlug: product?.fields?.urlSlug,
    productSlug: product?.fields?.productSlug,
    productUrl: allProductSlugs?.find((productSlug) =>
      productSlug.includes(`/${product?.fields?.urlSlug}/`)
    ),
    displayName: product?.fields?.displayName,
    productType: product?.fields?.productType || null,
    categoryCardPlusCta: product?.fields?.categoryCardPlusCta || null,
    image: {
      imageUrl:
        product?.fields?.productHeroImages?.[0]?.fields?.file?.url || '',
      imageTitle: product?.fields?.productHeroImages?.[0]?.fields?.title || '',
    },
    secondaryImage: {
      imageUrl:
        product?.fields?.productHeroImages?.[1]?.fields?.file?.url || '',
      imageTitle: product?.fields?.productHeroImages?.[1]?.fields?.title || '',
    },
    imageBackgroundColour: product?.fields?.productPageBackgroundColour,
    optionToShow: product?.fields?.productSelectionFields
      ?.map((field) => {
        return field?.showInCategoryPage
          ? { optionName: field?.name, optionTitle: field?.title }
          : undefined
      })
      ?.filter(Boolean)?.[0] || {
      optionName: 'size',
      optionTitle: 'Size',
    },
    moq: product?.fields?.productInfoMoq || 0,
    statusTag: mapTagEntry(product?.fields?.statusTag),
    saleTag: mapTagEntry(product?.fields?.saleTag),
    productTags: product?.fields?.productTags?.map((tag) => mapTagEntry(tag)),
    productDeliveryTypes:
      product?.fields?.productDeliveryTypes?.map((infoBlock) => {
        return {
          ...infoBlock?.fields,
          deliveryTypeIcon: mapContentfulImageAsset(
            infoBlock?.fields?.deliveryTypeIcon
          ),
        }
      }) || null,
  } as IProductInfoFromContentful
}

export async function getProductListFromCategory({
  categoryPageEntries,
  categories,
}: {
  categoryPageEntries: { fields: CategoryPage }
  categories: any
}) {
  const allProductSlugs = await loadAllProductSlugsFromContentful()
  const selectedProductCategoriesFromContentful: IProductInfoFromContentful[] =
    (
      categoryPageEntries?.fields?.productCards ||
      categoryPageEntries?.fields?.products
    )
      ?.map((entry) => {
        if (entry.sys.contentType.sys.id === 'productCategory') {
          return mapProductContentfulData(
            entry,
            allProductSlugs
          ) as IProductInfoFromContentful
        }
      })
      .filter(Boolean)

  const selectedProductCategories = hydrateSelectedProductCategoriesWithBcData(
    selectedProductCategoriesFromContentful,
    categories
  )

  // TODO: Should batch more to avoid hammering BC with price request.
  //  Turn it into sequential right now for some delays as interim
  const selectedProductCategoriesWithVariants = await Promise.all(
    selectedProductCategories?.map(async (productCategory) => {
      const productVariants = await getProductsWithPrice(
        productCategory.productSlug
      )
      return { ...productCategory, variants: productVariants }
    })
  )

  return selectedProductCategoriesWithVariants
}
